import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useBranding } from "../../context/BrandingContext";
import ImageUploader from "../../components/common/ImageUploader";
import TrustBadgesEditor from "../../components/admin/TrustBadgesEditor";
import { Switch } from "../../components/ui/switch";
import { Eye, Loader2 } from "lucide-react";

// Default TEXT shown as input placeholders. Deliberately no `image_url`:
// there is no longer a default image anywhere. The storefront resolves the
// section image as
//     configured image -> this tenant's own logo -> no image at all
// (see `resolveBrandImage` in context/BrandingContext.jsx). Carrying a stock
// Unsplash URL here implied a fallback that no longer exists, and the photos
// it pointed at belonged to one vertical — they rendered on every unrelated
// tenant's storefront.
//
// The text below mirrors `pages/store/Home.jsx`. Themes with a bespoke home
// (currently the `asfashion` preset) substitute their own neutral, brand-
// derived copy, so treat these as indicative rather than exact.
const FEATURED_DEFAULTS = {
  overline: "Editor's Pick",
  title: "Mega Deals across every department",
  body: "Save up to 70% on smartphones, laptops, kitchen appliances and beauty. Genuine products, fast shipping, and easy 7-day returns — every single day.",
  cta_label: "Shop the Deals",
  cta_link: "/shop?sort=discount",
};
const BRAND_STORY_DEFAULTS = {
  overline: "The {brand} Promise",
  title: "Genuine products, delivered fast.",
  body: "{brand} brings together thousands of verified sellers in one marketplace — so you get authentic products, transparent prices and a reliable delivery experience on everything from the latest smartphones to everyday kitchen essentials.",
  cta_label: "Become a seller",
  cta_link: "/seller/onboard",
};

const EMPTY_BLOCK = {
  overline: "", title: "", body: "",
  cta_label: "", cta_link: "", image_url: "",
};

// The rails that admins can toggle on/off entirely.
const TOGGLES = [
  { key: "categories",  label: "Category grid",            hint: "Auto-rendered tiles from your category tree." },
  { key: "campaigns",   label: "Campaigns rail",           hint: "Pulls from your active campaigns." },
  { key: "brands",      label: "Brands rail",              hint: "Auto-rendered from your brand catalog." },
  { key: "social",      label: "Social / Reels rail",      hint: "Instagram-style reels on the homepage. Controls layout only — availability of Reels is set by the platform team." },
  { key: "trending",    label: "Trending products grid",   hint: "Shows the 8 newest products." },
  { key: "featured",    label: "Featured / Editor's Pick", hint: "Large image + CTA section (editable below)." },
  { key: "brand_story", label: "Brand Story / Become a seller", hint: "Half-image / half-promise section (editable below)." },
];

function BlockEditor({ title, defaults, value, onChange, onSave, saving, testid }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4" data-testid={`${testid}-editor`}>
      <h3 className="font-display text-xl">{title}</h3>

      <div>
        <label className="overline block mb-1">
          Section image <span className="normal-case opacity-60">(optional)</span>
        </label>
        <ImageUploader
          value={value.image_url ? [value.image_url] : []}
          onChange={(arr) => set("image_url", arr[0] || "")}
          max={1}
          aspect="aspect-[16/10]"
          label="Upload image"
          testid={`${testid}-image`}
        />
        <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1 leading-relaxed"
           data-testid={`${testid}-image-hint`}>
          Upload an image and it is used here. Leave it blank and the storefront
          falls back to your store logo (set under <strong>Branding</strong>). If
          you have no logo either, this section simply renders without an image —
          no stock photo is ever shown.
        </p>
      </div>

      <div>
        <label className="overline block mb-1">Overline (small label above title)</label>
        <input
          className="kb-input"
          value={value.overline}
          onChange={(e) => set("overline", e.target.value)}
          placeholder={defaults.overline}
          maxLength={120}
          data-testid={`${testid}-overline`}
        />
      </div>

      <div>
        <label className="overline block mb-1">Title</label>
        <input
          className="kb-input"
          value={value.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder={defaults.title}
          maxLength={180}
          data-testid={`${testid}-title`}
        />
      </div>

      <div>
        <label className="overline block mb-1">Body copy</label>
        <textarea
          rows={3}
          className="kb-input"
          value={value.body}
          onChange={(e) => set("body", e.target.value)}
          placeholder={defaults.body}
          maxLength={600}
          data-testid={`${testid}-body`}
        />
      </div>

      <div className="border-t border-[hsl(var(--kb-border))] pt-4">
        <div className="overline mb-3">Call-to-action button</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            className="kb-input"
            value={value.cta_label}
            onChange={(e) => set("cta_label", e.target.value)}
            placeholder={defaults.cta_label}
            maxLength={40}
            data-testid={`${testid}-cta-label`}
          />
          <input
            className="kb-input"
            value={value.cta_link}
            onChange={(e) => set("cta_link", e.target.value)}
            placeholder={defaults.cta_link}
            maxLength={200}
            data-testid={`${testid}-cta-link`}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          className="kb-btn-primary"
          disabled={saving}
          onClick={onSave}
          data-testid={`${testid}-save`}
        >
          {saving ? "Saving…" : "Save section"}
        </button>
      </div>
    </div>
  );
}

export default function AdminHomeSections() {
  const { refresh: refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [savingFeatured, setSavingFeatured] = useState(false);
  const [savingBrandStory, setSavingBrandStory] = useState(false);
  const [savingVis, setSavingVis] = useState(null); // key being toggled

  const [vis, setVis] = useState({
    categories: true, campaigns: true, brands: true, social: true,
    trending: true, featured: true, brand_story: true,
  });
  const [featured, setFeatured] = useState(EMPTY_BLOCK);
  const [brandStory, setBrandStory] = useState(EMPTY_BLOCK);
  // Read-only. Reels availability is a super-admin decision stored on the
  // tenant record; this page cannot change it and never PUTs it. Shown so
  // the "Social / Reels rail" switch below doesn't look broken when the
  // rail is suppressed upstream.
  const [reelsAvailable, setReelsAvailable] = useState(true);
  // This store's active theme, so the Store benefits editor can say whether
  // the theme actually draws that strip.
  const [themePreset, setThemePreset] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/branding");
      const v = data.home_sections_visibility || {};
      setVis({
        categories: v.categories !== false,
        campaigns:  v.campaigns  !== false,
        brands:     v.brands     !== false,
        social:     v.social     !== false,
        trending:   v.trending   !== false,
        featured:   v.featured   !== false,
        brand_story: v.brand_story !== false,
      });
      const f = data.home_featured || {};
      setFeatured({
        overline: f.overline || "", title: f.title || "", body: f.body || "",
        cta_label: f.cta_label || "", cta_link: f.cta_link || "",
        image_url: f.image_url || "",
      });
      setReelsAvailable(data.reels_enabled !== false);
      setThemePreset(data.theme_preset);
      const bs = data.home_brand_story || {};
      setBrandStory({
        overline: bs.overline || "", title: bs.title || "", body: bs.body || "",
        cta_label: bs.cta_label || "", cta_link: bs.cta_link || "",
        image_url: bs.image_url || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load home sections");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleVis = async (key) => {
    const next = { ...vis, [key]: !vis[key] };
    setVis(next);
    setSavingVis(key);
    try {
      await api.put("/admin/branding/home-sections-visibility", { [key]: next[key] });
      toast.success(`${next[key] ? "Showing" : "Hiding"} the ${key.replace("_", " ")} section`);
      await refreshBranding?.();
    } catch (e) {
      // Revert UI state on failure
      setVis(vis);
      toast.error(e?.response?.data?.detail || "Could not save visibility");
    } finally {
      setSavingVis(null);
    }
  };

  const saveFeatured = async () => {
    setSavingFeatured(true);
    try {
      await api.put("/admin/branding/home-featured", featured);
      toast.success("Featured section saved");
      await refreshBranding?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingFeatured(false);
    }
  };
  const saveBrandStory = async () => {
    setSavingBrandStory(true);
    try {
      await api.put("/admin/branding/home-brand-story", brandStory);
      toast.success("Brand Story saved");
      await refreshBranding?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingBrandStory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[hsl(var(--kb-muted))]" data-testid="home-sections-loading">
        <Loader2 size={14} className="animate-spin" /> Loading home sections…
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-home-sections-page">
      <div className="overline mb-2">Storefront</div>
      <div className="flex items-center justify-between gap-4 mb-8">
        <h1 className="font-display text-4xl">Home Page Sections</h1>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="kb-btn-outline !py-2 !px-4 inline-flex items-center gap-2 text-sm"
          data-testid="home-sections-preview-link"
        >
          <Eye size={14} /> View storefront
        </a>
      </div>

      {/* Visibility toggles --------------------------------------------- */}
      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-8" data-testid="visibility-toggles">
        <h2 className="font-display text-xl mb-1">Show / hide rails</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-4">
          Turn off any rail to hide it on every storefront page. The Hero
          itself is configured under <em>Homepage Hero</em>.
        </p>
        <div className="divide-y divide-[hsl(var(--kb-border))]">
          {TOGGLES.map((t) => {
            const reelsBlocked = t.key === "social" && !reelsAvailable;
            return (
              <div key={t.key} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{t.label}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">{t.hint}</div>
                  {reelsBlocked && (
                    <div
                      className="text-xs text-amber-700 mt-1"
                      data-testid="reels-unavailable-notice"
                    >
                      Reels are currently turned off for this store by the
                      platform team, so this rail will not appear on your
                      storefront whatever this switch is set to. Your existing
                      reels are safe and will come back if it is turned on again.
                    </div>
                  )}
                </div>
                <Switch
                  checked={vis[t.key]}
                  disabled={savingVis === t.key || reelsBlocked}
                  onCheckedChange={() => toggleVis(t.key)}
                  data-testid={`toggle-${t.key}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Featured (Editor's Pick / Shop the Deals) --------------------- */}
      <div className="mb-8">
        <BlockEditor
          title="Featured / Editor's Pick"
          defaults={FEATURED_DEFAULTS}
          value={featured}
          onChange={setFeatured}
          onSave={saveFeatured}
          saving={savingFeatured}
          testid="featured"
        />
      </div>

      {/* Brand Story (Become a seller) -------------------------------- */}
      <BlockEditor
        title="Brand Story / Become a seller"
        defaults={BRAND_STORY_DEFAULTS}
        value={brandStory}
        onChange={setBrandStory}
        onSave={saveBrandStory}
        saving={savingBrandStory}
        testid="brand-story"
      />

      {/* Store benefits (Free Shipping · Premium Quality · Secure Payment) */}
      <div className="mt-10">
        <TrustBadgesEditor themePreset={themePreset} onSaved={refreshBranding} />
      </div>

      <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-6">
        Tip: leave any field blank to fall back to its default content.
      </p>
    </div>
  );
}
